import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { ApiHttpService } from "./services/api-http.service";
import { DataStorageService } from "./services/data-storage.service";
import { AccountService } from "./services/account.service";
import { CognitoAuthenticatedUser } from "./models";
import { filter, Observable, Subject, take, takeUntil, switchMap } from "rxjs";
import { IconRegistryService } from "./services/icon-registry.service";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, ParamMap, Params } from "@angular/router";
import { DataLoadService } from "./services/data-load.service";
import { LoadingService } from "./services/loading.service";
import { GoogleLinkService } from "./services/google-link.service";
import { SocialSignInService } from "./services/social-sign-in.service";

@Component({
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>
    @if (loadingService.loading$ | async) {
      <app-loading-screen class="loading-modal"></app-loading-screen>
    }
    @if ((dataLoadService.state$ | async) === 'error') {
      <app-load-error class="loading-modal"></app-load-error>
    }
    `,
  styles: [`
    app-loading-screen,
    app-load-error {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly supportedLanguages = ['en', 'de', 'fr', 'it'];
  private readonly fallbackLanguage = 'de';

  user$: Observable<CognitoAuthenticatedUser | null> | null = null;
  title = 'Calingo';

  constructor(
    private iconRegistryService: IconRegistryService,
    private accountService: AccountService,
    private dataStorageService: DataStorageService,
    private apiHttpService: ApiHttpService,
    private translate: TranslateService,
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    public dataLoadService: DataLoadService,
    private googleLinkService: GoogleLinkService,
    private socialSignInService: SocialSignInService,
    public loadingService: LoadingService
  ) {
    this.initializeServices();
    this.setupRouterSubscription();
    this.initializeLanguageOnNavigation();
  }

  async ngOnInit(): Promise<void> {
    this.setupTranslationChangeHandler();
    this.setupQueryParamHandler();
    this.setupUserSubscription();

    try {
      await this.accountService.handleFetchUserAttributes();
    } finally {
      this.loadingService.bootstrapComplete();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeServices(): void {
    this.iconRegistryService.init();
    this.titleService.setTitle(this.title);
    this.user$ = this.accountService.user$;

    this.translate.addLangs(this.supportedLanguages);
    this.translate.setFallbackLang(this.fallbackLanguage);

    // The router has not navigated yet at construction time, so `?lang` is read straight off
    // `window.location`. Waiting for the first NavigationEnd would leave the loading screen
    // rendering in the previous language until bootstrap happens to be slow enough.
    const queryLang = this.getUrlLanguage();
    const savedLang = this.getSavedLanguage();
    const browserLang = this.getBrowserLanguage();
    this.translate.use(queryLang || savedLang || browserLang || this.fallbackLanguage);

    // Same reasoning as above, and for one more: the account link callback is a
    // cold load coming back from Google, and the query string has to be read
    // before the router turns the URL into route segments.
    this.googleLinkService.captureCallbackFromUrl();

    // And for the same reason: Cognito refuses a Google sign-in on the OAuth
    // return path, with the code on `error_description`. The login screen has
    // to read it after the router has already turned the URL into segments.
    this.socialSignInService.captureRefusalFromUrl();
  }

  private setupRouterSubscription(): void {
    this.router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationStart) => {
      this.handleUrlChange(event.url);
    });
  }

  private initializeLanguageOnNavigation(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      take(1)
    ).subscribe(() => {
      this.initializeLanguage();
    });
  }

  private initializeLanguage(): void {
    const queryLang = this.toSupportedLanguage(this.route.snapshot.queryParamMap.get('lang')) || this.getUrlLanguage();
    const detectedLanguage = queryLang || this.getSavedLanguage() || this.getBrowserLanguage();
    const selectedLanguage = detectedLanguage || this.fallbackLanguage;

    this.translate.use(selectedLanguage);
    // Don't persist the fallback — that would pin the user to it even once their browser
    // reports a supported language.
    if (detectedLanguage) {
      localStorage.setItem('lang', detectedLanguage);
    }
    this.getBreedsForLanguage(selectedLanguage);
  }

  private getBrowserLanguage(): string | null {
    const browserLang = this.translate.getBrowserLang();
    return this.toSupportedLanguage(browserLang ?? null);
  }

  private getUrlLanguage(): string | null {
    return this.toSupportedLanguage(new URLSearchParams(window.location.search).get('lang'));
  }

  private getSavedLanguage(): string | null {
    return this.toSupportedLanguage(localStorage.getItem('lang'));
  }

  private toSupportedLanguage(lang: string | null): string | null {
    return lang && this.supportedLanguages.includes(lang) ? lang : null;
  }

  private setupTranslationChangeHandler(): void {
    this.translate.onLangChange.pipe(
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      this.updateTitle();
      this.getBreedsForLanguage(event.lang);
      localStorage.setItem('lang', event.lang);
    });
  }

  private updateTitle(): void {
    this.translate.get('common.application-tab-title').pipe(
      take(1)
    ).subscribe((title: string) => {
      this.title = title;
      this.titleService.setTitle(this.title);
    });
  }

  private setupQueryParamHandler(): void {
    this.route.queryParamMap.pipe(
      takeUntil(this.destroy$)
    ).subscribe((params: ParamMap) => {
      const langParam = this.toSupportedLanguage(params.get('lang'));
      if (langParam) {
        this.translate.use(langParam);
        localStorage.setItem('lang', langParam);
      }
    });
  }

  private setupUserSubscription(): void {
    this.user$?.pipe(
      filter(user => !!user),
      switchMap(() => this.ensureDataLoaded()),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  private async ensureDataLoaded(): Promise<void> {
    if (this.dataStorageService.loaded) {
      return;
    }
    try {
      await this.dataLoadService.initLoading();
      this.handleContractLanguage();
    } catch (err) {
      console.error('Initial data load failed', err);
    }
  }

  private handleContractLanguage(): void {
    const langExplicitlySet = localStorage.getItem('explicitly-set-lang');
    if (langExplicitlySet === 'true') {
      return;
    }

    const contract = this.dataStorageService.getAllData.find(petData => !!petData.lang);
    if (contract && this.supportedLanguages.includes(contract.lang!)) {
      this.translate.use(contract.lang!);
    }
  }

  private async getBreedsForLanguage(lang: string): Promise<void> {
    try {
      const [catBreeds, dogBreeds] = await Promise.all([
        this.apiHttpService.getBreed('cat'),
        this.apiHttpService.getBreed('dog')
      ]);

      this.dataStorageService.catBreeds = catBreeds;
      this.dataStorageService.dogBreeds = dogBreeds;
    } catch (error) {
      console.error('Error loading breeds:', error);
    }
  }

  private handleUrlChange(url: string): void {
    // The mobile (`calingo/...`) and desktop (`desktop/home/...`) route trees share the same
    // structure, so switching platforms on resize is a straight prefix swap.
    const [, platform, ...restArr] = url.split('/');

    if (platform === 'desktop' && window.innerWidth < 1024) {
      // desktop/home/<rest>  ->  calingo/<rest>
      const [, ...calingoRest] = restArr; // drop the leading `home` segment
      const newUrl = ['calingo', ...calingoRest];

      this.router.navigate(newUrl);
      return;
    }

    if (platform === 'calingo' && window.innerWidth >= 1024) {
      // calingo/<rest>  ->  desktop/home/<rest>
      const newUrl = ['desktop', 'home', ...restArr];

      this.router.navigate(newUrl);
    }
  }
}
